/**
 * Browser-geolocation capture for the current session.
 *
 * Asks the browser's HTML5 Geolocation API for a position fix once per
 * authenticated session and POSTs it to /api/sessions/me/geo. Whether the
 * user grants or denies, we record the consent state so the Sessions tab
 * can show a real consent-rate stat and we don't re-prompt within a session.
 *
 * Cached consent in localStorage means: granted users get a silent fix on
 * every login, denied users are skipped entirely (no prompt), unsupported
 * browsers are reported once and forgotten. They can clear localStorage
 * to be re-asked.
 */

const CONSENT_KEY = 'pulse-geo-consent';
const TIMEOUT_MS = 10_000;

type ConsentState = 'granted' | 'denied' | 'unsupported';

interface CachedConsent {
  state: ConsentState;
  decided_at: number; // ms since epoch
}

function readCachedConsent(): CachedConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedConsent;
    if (parsed.state !== 'granted' && parsed.state !== 'denied' && parsed.state !== 'unsupported') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeCachedConsent(state: ConsentState): void {
  try {
    const payload: CachedConsent = { state, decided_at: Date.now() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  } catch {
    // localStorage disabled (private mode / cookies blocked) — best-effort.
  }
}

async function postGeoToServer(payload: {
  consent: ConsentState;
  lat?: number;
  lng?: number;
  accuracy_m?: number;
}): Promise<void> {
  // Fire-and-forget — failures here mustn't disrupt the user. We log a
  // warning so a debug session can find it, but the UI never sees it.
  try {
    const res = await fetch('/api/sessions/me/geo', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      console.warn('[geo] POST failed', res.status);
    }
  } catch (err) {
    console.warn('[geo] POST error', err);
  }
}

function requestPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: TIMEOUT_MS,
      maximumAge: 60_000, // accept a cached fix from the last minute
    });
  });
}

/**
 * Called once after the user lands on an authenticated page. Decides:
 *  - cached granted → fetch position silently and POST
 *  - cached denied → POST 'denied' so server has fresh state, no prompt
 *  - cached unsupported → no-op (browser told us last time)
 *  - no cache → prompt the user via getCurrentPosition; cache the result
 */
export async function captureGeoIfNeeded(): Promise<void> {
  const cached = readCachedConsent();

  if (cached?.state === 'denied') {
    // Honour the prior denial — don't re-prompt. Re-report so the server
    // knows this session also denied (useful for the consent-rate stat).
    void postGeoToServer({ consent: 'denied' });
    return;
  }
  if (cached?.state === 'unsupported') {
    void postGeoToServer({ consent: 'unsupported' });
    return;
  }

  if (!('geolocation' in navigator)) {
    writeCachedConsent('unsupported');
    void postGeoToServer({ consent: 'unsupported' });
    return;
  }

  try {
    const pos = await requestPosition();
    writeCachedConsent('granted');
    await postGeoToServer({
      consent: 'granted',
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy_m: pos.coords.accuracy,
    });
  } catch (err) {
    // PERMISSION_DENIED is the canonical user-clicked-no case. TIMEOUT
    // and POSITION_UNAVAILABLE mean the browser supports the API but
    // couldn't pin the user — treat both as 'denied' so we stop prompting.
    const code = (err as GeolocationPositionError)?.code;
    if (code === 1) {
      // PERMISSION_DENIED — user clicked block
      writeCachedConsent('denied');
      void postGeoToServer({ consent: 'denied' });
    } else {
      // TIMEOUT / POSITION_UNAVAILABLE — treat as unsupported for now;
      // user can clear localStorage to retry.
      writeCachedConsent('unsupported');
      void postGeoToServer({ consent: 'unsupported' });
    }
  }
}

/** Admin helper — wipes cached consent so the next page load re-prompts. */
export function resetGeoConsent(): void {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    // ignore
  }
}
